import fs from 'fs'
import path from 'path'

import type { ProviderLayout } from '../../constants'
import {
  AI_PROVIDERS_DIRECTORY,
  CLAUDE_PROVIDER,
  ORCHESTRATOR_SEGMENT_FILE,
  PROVIDER_AGENTS_DIRECTORY,
  PROVIDERS,
} from '../../constants'
import { AiDocsMissingError } from '../../errors'
import { packageRoot } from '../../templates/package-root'
import type { OperationOptions } from '../types'
import { emitter } from '../types'
import type { SegmentOutcome } from './segment'
import { mergeSegment } from './segment'

// `init-claude` connects a project to the FlowRunner agent instructions the CLI ships.
//
// Which files it writes, and where, comes from the provider layout in `constants/providers.ts` — Claude Code
// reads `CLAUDE.md` and not `AGENTS.md`, and the next provider will differ again, so that knowledge is data
// rather than something `init` could write once for everyone.
//
// Two halves, and they differ in who owns the file. The agent definitions under the provider's agents
// directory are the CLI's: they are named `flowrunner-service-*`, they mean nothing without the instructions
// they point at, and refreshing them on every run is the point of the command — so they are overwritten. The
// provider's instructions file is the project's, and only the region between the CLI's own markers is ever
// rewritten (see `segment.ts`).

export type InitClaudeEvent =
  | { type: 'agents'; directory: string; created: string[]; updated: string[]; unchanged: string[] }
  | { type: 'segment'; file: string; outcome: SegmentOutcome }
  | { type: 'warning'; message: string }

export type InitClaudeOptions = OperationOptions<InitClaudeEvent>

export interface InitClaudeResult {
  projectRoot: string
  /** Absolute path of the provider's agents directory. */
  agentsDirectory: string
  /** Agent file names, relative to `agentsDirectory`. */
  created: string[]
  updated: string[]
  unchanged: string[]
  /** Absolute path of the provider's instructions file. */
  claudeFile: string
  segment: SegmentOutcome
}

/** Where the packed docs for a provider live inside the installed package. */
export function providerDirectory(provider: string = CLAUDE_PROVIDER): string {
  return path.join(packageRoot(), AI_PROVIDERS_DIRECTORY, provider)
}

/**
 * The agent definitions the installed package ships, sorted so the reported list is stable.
 *
 * Only top-level `.md` files: an agent definition is one file, and anything else in there is not one.
 */
function packedAgents(directory: string): string[] {
  if (!fs.existsSync(directory)) {
    return []
  }

  return fs.readdirSync(directory, { withFileTypes: true })
    .filter(entry => entry.isFile() && entry.name.endsWith('.md'))
    .map(entry => entry.name)
    .sort()
}

/**
 * Write the shipped agent definitions into `.claude/agents/`.
 *
 * Overwrites. These files are CLI-owned — a customer editing one would have it replaced by the next
 * `init-claude`, which is the same deal as any other generated file, and the alternative (skip what exists)
 * makes the command unable to ship a fix. Identical content is reported as `unchanged` rather than written,
 * so a re-run does not churn mtimes for no reason.
 */
function writeAgents(sourceDirectory: string, projectRoot: string, layout: ProviderLayout): {
  directory: string
  created: string[]
  updated: string[]
  unchanged: string[]
  warnings: string[]
} {
  const directory = path.join(projectRoot, layout.agentsDirectory)
  const agents = packedAgents(sourceDirectory)

  const created: string[] = []
  const updated: string[] = []
  const unchanged: string[] = []
  const warnings: string[] = []

  if (agents.length === 0) {
    warnings.push(`The installed CLI ships no agent definitions in ${ PROVIDER_AGENTS_DIRECTORY }/ — none were written.`)

    return { directory, created, updated, unchanged, warnings }
  }

  fs.mkdirSync(directory, { recursive: true })

  for (const agent of agents) {
    const contents = fs.readFileSync(path.join(sourceDirectory, agent), 'utf-8')
    const target = path.join(directory, agent)

    if (contents.length === 0) {
      // Shipped-but-unwritten: the file still lands, so the wiring is in place and filling it later is a
      // CLI upgrade rather than another manual step. Saying so is what stops it looking like a broken agent.
      warnings.push(`${ agent } is empty in the installed CLI — written as an empty file.`)
    }

    if (!fs.existsSync(target)) {
      fs.writeFileSync(target, contents, 'utf-8')
      created.push(agent)

      continue
    }

    if (fs.readFileSync(target, 'utf-8') === contents) {
      unchanged.push(agent)

      continue
    }

    fs.writeFileSync(target, contents, 'utf-8')
    updated.push(agent)
  }

  return { directory, created, updated, unchanged, warnings }
}

/** Merge the orchestrator segment into the provider's own instructions file. */
function writeSegment(sourceDirectory: string, projectRoot: string, layout: ProviderLayout): {
  file: string
  outcome: SegmentOutcome
  warnings: string[]
} {
  const file = path.join(projectRoot, layout.instructionsFile)
  const source = path.join(sourceDirectory, ORCHESTRATOR_SEGMENT_FILE)
  const warnings: string[] = []

  if (!fs.existsSync(source)) {
    warnings.push(`The installed CLI ships no ${ ORCHESTRATOR_SEGMENT_FILE } — ${ layout.instructionsFile } was left alone.`)

    return { file, outcome: 'unchanged', warnings }
  }

  const segment = fs.readFileSync(source, 'utf-8')

  if (segment.trim().length === 0) {
    warnings.push(`${ ORCHESTRATOR_SEGMENT_FILE } is empty in the installed CLI — the marked block is empty.`)
  }

  const current = fs.existsSync(file) ? fs.readFileSync(file, 'utf-8') : null
  const merged = mergeSegment(current, segment, layout.instructionsFile)

  if (merged.outcome !== 'unchanged') {
    fs.writeFileSync(file, merged.contents, 'utf-8')
  }

  return { file, outcome: merged.outcome, warnings }
}

/**
 * Wire the shipped FlowRunner agent instructions into a project's Claude setup.
 *
 * Deliberately does not require a FlowRunner project: someone can reasonably run this in a repo before
 * `init`, and refusing would be a rule with nothing behind it — nothing here reads `flowrunner.json`.
 */
export async function initClaude(options: InitClaudeOptions = {}): Promise<InitClaudeResult> {
  const projectRoot = path.resolve(options.cwd ?? '.')
  const emit = emitter(options)
  // Each event first, then the warnings about it: a warning printed before its own heading reads as a
  // warning about whatever came before.
  const warnAll = (messages: string[]) => messages.forEach(message => emit({ type: 'warning', message }))

  const layout = PROVIDERS[CLAUDE_PROVIDER]
  const sourceDirectory = providerDirectory(layout.id)

  if (!fs.existsSync(sourceDirectory)) {
    throw new AiDocsMissingError(path.join(AI_PROVIDERS_DIRECTORY, CLAUDE_PROVIDER))
  }

  if (!fs.existsSync(projectRoot)) {
    fs.mkdirSync(projectRoot, { recursive: true })
  }

  const agents = writeAgents(path.join(sourceDirectory, PROVIDER_AGENTS_DIRECTORY), projectRoot, layout)

  emit({
    type     : 'agents',
    directory: agents.directory,
    created  : agents.created,
    updated  : agents.updated,
    unchanged: agents.unchanged,
  })

  warnAll(agents.warnings)

  const segment = writeSegment(sourceDirectory, projectRoot, layout)

  emit({ type: 'segment', file: segment.file, outcome: segment.outcome })

  warnAll(segment.warnings)

  return {
    projectRoot,
    agentsDirectory: agents.directory,
    created        : agents.created,
    updated        : agents.updated,
    unchanged      : agents.unchanged,
    claudeFile     : segment.file,
    segment        : segment.outcome,
  }
}

export type { MergeResult, SegmentOutcome } from './segment'
export { BEGIN_MARKER, END_MARKER, mergeSegment } from './segment'
